import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { away, builderNext, keptNext, safeNext } from "@/auth/session";
import { AuthLayout } from "@/components/layout";
import { RegistrationTerms } from "@/components/legal";
import { Alert, Button, ErrorAlert, Field, Form, Input, PasswordInput } from "@/components/ui";
import { auth, portal, session } from "@/lib/api";
import { loginErrorMessage } from "@/lib/reauth";
import type { LoginResult, TokensOut } from "@/lib/types";

const OAUTH_ERRORS: Record<string, string> = {
  access_denied: "Sign-in was cancelled.",
  account_suspended: "This account has been suspended.",
};

/** Continue after tokens were issued: adopt them and go where the user was headed. */
function useFinish() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (tokens: TokensOut) => {
    session.accept(tokens);
    session.announceSignIn();
    const next = params.get("next");
    const builder = builderNext(next);
    if (builder) away.go(builder);
    else navigate(safeNext(next), { replace: true });
  };
}

function MfaStep({ challenge, onCancel }: { challenge: string; onCancel: () => void }) {
  const finish = useFinish();
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const verify = useMutation({
    mutationFn: () =>
      auth.loginMfa(challenge, useRecovery ? { recovery_code: code.trim() } : { code: code.trim() }),
    onSuccess: finish,
  });
  return (
    <AuthLayout
      title="Two-factor authentication"
      subtitle={
        useRecovery
          ? "Enter one of your recovery codes."
          : "Enter the 6-digit code from your authenticator app."
      }
    >
      <Form
        className="space-y-4"
        error={verify.error}
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
      >
        {verify.error && (
          <Alert tone="danger">{loginErrorMessage(verify.error) ?? verify.error.message}</Alert>
        )}
        <Field label={useRecovery ? "Recovery code" : "Authentication code"} name="code" required>
          {(p) => (
            <Input
              {...p}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
              autoFocus
              maxLength={useRecovery ? 32 : 6}
              pattern={useRecovery ? undefined : "[0-9]{6}"}
              title={useRecovery ? undefined : "Enter the 6-digit code from your authenticator app"}
            />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={verify.isPending}>
          Verify
        </Button>
        <div className="flex justify-between text-sm">
          <Button
            unstyled
            type="button"
            className="text-brand-700 hover:underline dark:text-brand-500"
            onClick={() => setUseRecovery((v) => !v)}
          >
            {useRecovery ? "Use authenticator code" : "Use a recovery code"}
          </Button>
          <Button unstyled type="button" className="text-fg-muted hover:underline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <p className="text-center text-sm text-fg-muted">
          Lost your authenticator and your recovery codes?{" "}
          <Link to="/mfa-recovery" className="text-brand-700 hover:underline dark:text-brand-500">
            Recover your account
          </Link>
        </p>
      </Form>
    </AuthLayout>
  );
}

export function LoginPage() {
  const finish = useFinish();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Federated sign-in returns with `#mfa_challenge=…` when the account has MFA enabled.
  const [challenge, setChallenge] = useState<string | null>(() =>
    new URLSearchParams(window.location.hash.slice(1)).get("mfa_challenge"),
  );
  const providers = useQuery({
    queryKey: ["auth-providers"],
    queryFn: auth.providers,
    staleTime: Infinity,
    retry: false,
  });
  const terms = useQuery({
    queryKey: ["legal-registration-requirements"],
    queryFn: portal.registrationRequirements,
    enabled: (providers.data?.providers.length ?? 0) > 0,
    staleTime: 60_000,
  });
  const login = useMutation({
    mutationFn: () => auth.login(email.trim(), password),
    onSuccess: (result: LoginResult) => {
      if (result.status === "mfa_required" && "challenge_token" in result)
        setChallenge(result.challenge_token);
      else finish(result as TokensOut);
    },
  });

  if (challenge) return <MfaStep challenge={challenge} onCancel={() => setChallenge(null)} />;

  const oauthError = params.get("error");
  const next = keptNext(params.get("next"));
  const returnTo = `${window.location.origin}/login${next ? `?next=${encodeURIComponent(next)}` : ""}`;
  const acceptedDocuments =
    terms.data?.documents.map(({ document_key, version }) => ({ document_key, version })) ?? [];
  const oauthTermsReady = terms.isSuccess && (acceptedDocuments.length === 0 || termsAccepted);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout title="Sign in" subtitle="One account for every CortexGrip product.">
      <Form className="space-y-4" onSubmit={onSubmit} error={login.error}>
        {oauthError && (
          <Alert tone="danger">{OAUTH_ERRORS[oauthError] ?? "Sign-in with that provider failed."}</Alert>
        )}
        {login.error &&
          (loginErrorMessage(login.error) ? (
            <Alert tone="danger">{loginErrorMessage(login.error)}</Alert>
          ) : (
            <ErrorAlert error={login.error} />
          ))}
        <Field label="E-mail" name="email" required>
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
              autoFocus
            />
          )}
        </Field>
        <Field label="Password" name="password" required>
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <div className="flex justify-end text-sm">
          <Link to="/forgot-password" className="text-brand-700 hover:underline dark:text-brand-500">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" className="w-full" loading={login.isPending}>
          Sign in
        </Button>
      </Form>

      {providers.data && providers.data.providers.length > 0 && (
        <div className="mt-6 space-y-2">
          {terms.data && (
            <RegistrationTerms
              documents={terms.data.documents}
              checked={termsAccepted}
              onChange={setTermsAccepted}
            />
          )}
          {terms.isPending && <p className="text-sm text-fg-muted">Loading the current terms…</p>}
          {terms.error && <ErrorAlert error={terms.error} />}
          <div className="flex items-center gap-3 text-xs text-fg-subtle">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>
          {oauthTermsReady ? (
            providers.data.providers.map((p) => (
              <a
                key={p}
                href={auth.oauthUrl(p, returnTo, acceptedDocuments)}
                className="flex h-10 w-full items-center justify-center rounded-lg border border-border text-sm font-medium text-fg hover:bg-muted"
              >
                Continue with {p === "github" ? "GitHub" : p.charAt(0).toUpperCase() + p.slice(1)}
              </a>
            ))
          ) : (
            <p className="text-sm text-fg-muted">
              Read and accept the current terms above to continue with a provider.
            </p>
          )}
        </div>
      )}

      <p className="mt-6 text-center text-sm text-fg-muted">
        New to CortexGrip?{" "}
        <Link
          to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`}
          className="font-medium text-brand-700 hover:underline dark:text-brand-500"
        >
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
